'use client'

import { createContext, type ReactNode, useContext, useState } from 'react'
import { BrandGuidePicker } from '../app-visuals/brand-guide/brand-guide-picker'
import { BRAND_GUIDE_PICKS } from './brand-guide-picks'

type BrandGuideSelection = {
  activeKey: string
  isPlaying: boolean
  /** The visitor picked a brand: show it, and stop playing on its own. */
  select: (key: string) => void
  /** The timer ended: move to the next brand. */
  advance: () => void
}

const BrandGuideSelectionContext = createContext<BrandGuideSelection | null>(
  null
)

/**
 * Which brand the home brand guide shows, shared by the pills under the
 * section's subtitle and the visual below them (its scene loads later, in
 * its own chunk, and reads the key here). Renders no element of its own,
 * so the section's layout is unchanged. Starts on Brew and plays through
 * the brands until the visitor picks one.
 */
export function BrandGuideSelectionProvider({
  children,
}: {
  children: ReactNode
}) {
  const [activeKey, setActiveKey] = useState(
    () => BRAND_GUIDE_PICKS[0]?.key ?? ''
  )
  const [isPlaying, setIsPlaying] = useState(true)
  const select = (key: string) => {
    setIsPlaying(false)
    setActiveKey(key)
  }
  const advance = () => {
    const index = BRAND_GUIDE_PICKS.findIndex((pick) => pick.key === activeKey)
    const next = BRAND_GUIDE_PICKS[(index + 1) % BRAND_GUIDE_PICKS.length]
    if (next) {
      setActiveKey(next.key)
    }
  }
  return (
    <BrandGuideSelectionContext
      value={{ activeKey, isPlaying, select, advance }}
    >
      {children}
    </BrandGuideSelectionContext>
  )
}

/** The brand the guide shows; null outside the provider (the Paper lab). */
export function useBrandGuideKey(): string | null {
  return useContext(BrandGuideSelectionContext)?.activeKey ?? null
}

/** The brand pills, centered under the section's subtitle. */
export function BrandGuidePills() {
  const selection = useContext(BrandGuideSelectionContext)
  if (!selection) {
    return null
  }
  return (
    <BrandGuidePicker
      brands={BRAND_GUIDE_PICKS}
      activeKey={selection.activeKey}
      isPlaying={selection.isPlaying}
      onSelect={selection.select}
      onElapsed={selection.advance}
    />
  )
}
